import { ReactNode } from 'react';
import { Route, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppShell, LoginPage, NavItem, RequireAuth, useAuth, UserMenu } from '@greenrinse/ui';
import {
  BarChart3, CalendarDays, Car, ClipboardList, FileText, Globe, Handshake, History, Landmark, LayoutDashboard, Leaf, MapPin, MapPinned,
  MessageSquareWarning, Package, ReceiptText, Scale, Settings, TriangleAlert, UserCog, UsersRound,
} from 'lucide-react';
import DashboardPage from './pages/DashboardPage';
import SettingsPage from './pages/SettingsPage';
import PlaceholderPage from './pages/PlaceholderPage';
import CountriesPage from './pages/CountriesPage';
import LiveMapPage from './pages/LiveMapPage';
import PartnersPage from './pages/PartnersPage';
import PartnerDetailPage from './pages/PartnerDetailPage';
import SitesPage from './pages/SitesPage';
import FleetPage from './pages/FleetPage';
import ContractsPage from './pages/ContractsPage';
import TeamsPage from './pages/TeamsPage';
import UsersPage from './pages/UsersPage';
import AuditPage from './pages/AuditPage';
import RecordsPage from './pages/RecordsPage';
import ReconciliationPage from './pages/ReconciliationPage';
import PeriodDetailPage from './pages/PeriodDetailPage';
import DisputesPage from './pages/DisputesPage';
import InvoicesPage from './pages/InvoicesPage';
import ReportsPage from './pages/ReportsPage';
import InventoryPage from './pages/InventoryPage';
import AccountingPage from './pages/AccountingPage';
import { BrandingLoader, NotificationsBell, NotificationsPage, SchedulePage, SustainabilityPage, TourButton, TourPlayer } from '@greenrinse/features';
import { adminTour } from './tour';


function Layout({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const role = user?.role ?? '';
  const ic = { size: 18, strokeWidth: 2 };
  const S = { overview: t('nav.section_overview'), ops: t('nav.section_operations'), billing: t('nav.section_billing'), insights: t('nav.section_insights'), admin: t('nav.section_admin') };
  const nav: NavItem[] = [
    { to: '/', label: t('nav.dashboard'), end: true, icon: <LayoutDashboard {...ic} />, section: S.overview },
    { to: '/tracking', label: t('nav.tracking'), icon: <MapPinned {...ic} />, section: S.overview },
    { to: '/records', label: t('nav.records'), icon: <ClipboardList {...ic} />, section: S.ops },
    { to: '/exceptions', label: t('nav.exceptions'), icon: <TriangleAlert {...ic} />, section: S.ops },
    { to: '/schedule', label: t('nav.schedule'), icon: <CalendarDays {...ic} />, section: S.ops },
    { to: '/teams', label: t('nav.teams'), icon: <UsersRound {...ic} />, section: S.ops },
    { to: '/inventory', label: t('nav.inventory'), icon: <Package {...ic} />, section: S.ops },
    { to: '/partners', label: t('nav.partners'), icon: <Handshake {...ic} />, section: S.billing },
    { to: '/sites', label: t('nav.sites'), icon: <MapPin {...ic} />, section: S.billing },
    { to: '/fleet', label: t('nav.fleet'), icon: <Car {...ic} />, section: S.billing },
    { to: '/contracts', label: t('nav.contracts'), icon: <FileText {...ic} />, section: S.billing },
    { to: '/reconciliation', label: t('nav.reconciliation'), icon: <Scale {...ic} />, section: S.billing },
    { to: '/disputes', label: t('nav.disputes'), icon: <MessageSquareWarning {...ic} />, section: S.billing },
    { to: '/invoices', label: t('nav.invoices'), icon: <ReceiptText {...ic} />, section: S.billing },
    ...(['SuperAdmin', 'RegionalManager', 'Finance'].includes(role) ? [{ to: '/accounting', label: t('nav.accounting'), icon: <Landmark {...ic} />, section: S.billing }] : []),
    { to: '/reports', label: t('nav.reports'), icon: <BarChart3 {...ic} />, section: S.insights },
    { to: '/sustainability', label: t('nav.sustainability'), icon: <Leaf {...ic} />, section: S.insights },
    ...(role === 'SuperAdmin' || role === 'RegionalManager' ? [
      { to: '/users', label: t('nav.users'), icon: <UserCog {...ic} />, section: S.admin },
      { to: '/countries', label: t('nav.countries'), icon: <Globe {...ic} />, section: S.admin },
      { to: '/audit', label: t('nav.audit'), icon: <History {...ic} />, section: S.admin },
    ] : []),
    ...(role === 'SuperAdmin' ? [{ to: '/settings', label: t('nav.settings'), icon: <Settings {...ic} />, section: S.admin }] : []),
  ];
  return (
    <AppShell
      nav={nav}
      right={<><TourButton /><NotificationsBell /><UserMenu /></>}
    >
      <BrandingLoader />
      {children}
    </AppShell>
  );
}

export default function App() {
  return (
    <>
    <TourPlayer app="admin" chapters={adminTour} />
    <Routes>
      <Route path="/login" element={<LoginPage subtitle="Operator admin" demoKey="admin" />} />
      <Route
        path="/*"
        element={
          <RequireAuth>
            <Layout>
              <Routes>
                <Route index element={<DashboardPage />} />
                <Route path="partners" element={<PartnersPage />} />
                <Route path="partners/:id" element={<PartnerDetailPage />} />
                <Route path="sites" element={<SitesPage />} />
                <Route path="fleet" element={<FleetPage />} />
                <Route path="contracts" element={<ContractsPage />} />
                <Route path="teams" element={<TeamsPage />} />
                <Route path="records" element={<RecordsPage />} />
                <Route path="exceptions" element={<RecordsPage exceptionsOnly />} />
                <Route path="reconciliation" element={<ReconciliationPage />} />
                <Route path="reconciliation/:id" element={<PeriodDetailPage />} />
                <Route path="disputes" element={<DisputesPage />} />
                <Route path="invoices" element={<InvoicesPage />} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="notifications" element={<NotificationsPage swUrl={`${import.meta.env.BASE_URL}push-sw.js`} />} />
                <Route path="schedule" element={<SchedulePage />} />
                <Route path="tracking" element={<LiveMapPage />} />
                <Route path="inventory" element={<InventoryPage />} />
                <Route path="accounting" element={<RequireAuth roles={['SuperAdmin', 'RegionalManager', 'Finance']}><AccountingPage /></RequireAuth>} />
                <Route path="sustainability" element={<SustainabilityPage />} />
                <Route path="users" element={<RequireAuth roles={['SuperAdmin', 'RegionalManager']}><UsersPage /></RequireAuth>} />
                <Route path="countries" element={<RequireAuth roles={['SuperAdmin', 'RegionalManager']}><CountriesPage /></RequireAuth>} />
                <Route path="audit" element={<RequireAuth roles={['SuperAdmin', 'RegionalManager']}><AuditPage /></RequireAuth>} />
                <Route path="settings" element={<RequireAuth roles={['SuperAdmin']}><SettingsPage /></RequireAuth>} />
                <Route path="*" element={<PlaceholderPage />} />
              </Routes>
            </Layout>
          </RequireAuth>
        }
      />
    </Routes>
    </>
  );
}
